import { gql } from "@apollo/client";

// ============================================================================
// OFFICE QUERIES
// ============================================================================

import {
  GENERAL_FIELDS,
  SITE_FIELDS,
  PAGE_SETTINGS,
  FOOTER_OVERRIDES,
  OFFICE_LOCATION,
} from "./shared";
import { SECTIONS_WRAPPER_QUERY, EXPERTISE_FIELDS } from "./fragments/sections";
import { SERVICE_FIELDS } from "./fragments/services";

// ============================================================================
// OFFICE FRAGMENTS
// ============================================================================

export const OFFICE_FIELDS = gql`
  ${OFFICE_LOCATION}
  fragment OfficeFields on OfficeRecord {
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    externalLink
    googleBusinessUrl
    officeLocations {
      ...OfficeLocation
    }
    # Parent chain for nested office URLs (/offices/root/child/leaf)
    parent {
      slug
      parent {
        slug
        parent {
          slug
          parent {
            slug
          }
        }
      }
    }
    # Add titles and slugs for AppContext
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const OFFICE_INTRO_TEASER_IMAGE = gql`
  fragment OfficeIntroTeaserImage on OfficeRecord {
    officeImage {
      id
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "700", h: "240", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
    introImage {
      id
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "700", h: "240", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
  }
`;

export const OFFICE_TEASER_IMAGE = gql`
  fragment OfficeTeaserImage on OfficeRecord {
    introImage {
      id
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
  }
`;

export const OFFICES_OVERVIEW_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${OFFICE_FIELDS}
  ${OFFICE_INTRO_TEASER_IMAGE}
  query GetOffices($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    offices: allOffices(
      locale: $locale
      first: 100
      orderBy: _createdAt_DESC
      filter: { _locales: { allIn: [$locale] }, hideOfficeFromOverview: { eq: false } }
    ) {
      ...OfficeFields
      ...OfficeIntroTeaserImage
    }
  }
`;

export const OFFICE_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${OFFICE_FIELDS}
  ${PAGE_SETTINGS}
  ${SECTIONS_WRAPPER_QUERY}
  ${FOOTER_OVERRIDES}
  ${SERVICE_FIELDS}
  ${EXPERTISE_FIELDS}
  query GetOffice($slug: String!, $locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: office(filter: { slug: { eq: $slug } }, locale: $locale) {
      # Office Meta (SEO) — full parent chain for breadcrumbs and canonical URLs
      parent {
        slug
        hubspotNewsletterSegment
        slugs: _allSlugLocales(locale: $locale) {
          locale
          value
        }
        titles: _allTitleLocales(locale: $locale) {
          locale
          value
        }
        parent {
          slug
          hubspotNewsletterSegment
          slugs: _allSlugLocales(locale: $locale) {
            locale
            value
          }
          titles: _allTitleLocales(locale: $locale) {
            locale
            value
          }
          parent {
            slug
            hubspotNewsletterSegment
            slugs: _allSlugLocales(locale: $locale) {
              locale
              value
            }
            titles: _allTitleLocales(locale: $locale) {
              locale
              value
            }
            parent {
              slug
              hubspotNewsletterSegment
              slugs: _allSlugLocales(locale: $locale) {
                locale
                value
              }
              titles: _allTitleLocales(locale: $locale) {
                locale
                value
              }
            }
          }
        }
      }
      seoMetaTags: _seoMetaTags(locale: $locale) {
        attributes
        content
        tag
      }
      blockSearchIndexing

      # Office Fields (Content)
      ...OfficeFields

      # Intro Image - Full Width (Landscape 1560x500)
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      # Intro Image - Square (500x500) for when not full width
      introImageSquare: introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      introImageFullWidth
      officeVideo {
        url
        format
      }
      officeImagePiaLogo
      callToActions {
        ...ButtonBlock
        ...ButtonExternalBlock
        ...DownloadButtonBlock
        ...HubspotButtonBlock
      }
      footerOverrides {
        ...FooterOverrides
      }
      hubspotNewsletterSegment
      pageSettings {
        ...PageSettings
      }
      sections {
        ...SectionWrapperQuery
      }
      services {
        ...ServiceFields
      }
      expertises {
        ...ExpertiseFields
      }
    }
  }
`;

export const OFFICES_FOR_FORM_QUERY = gql`
  query GetOfficesForForm($locale: SiteLocale!) {
    offices: allOffices(
      first: 500
      filter: { parent: { exists: "false" }, _locales: { allIn: [$locale] } }
      locale: $locale
      orderBy: title_ASC
    ) {
      id
      title
      slug
      officeLocations {
        id
        slug
        title
        officeAddress
      }
      children {
        id
      }
    }
  }
`;

export const OFFICE_LANGUAGE_ID_QUERY = gql`
  query GetOfficeLanguageId($officeLanguage: String) {
    officeLanguage(filter: { slug: { eq: $officeLanguage } }) {
      id
    }
  }
`;

export const OFFICES_RANDOM_QUERY = gql`
  ${OFFICE_FIELDS}
  ${OFFICE_INTRO_TEASER_IMAGE}
  query GetRandomOffices(
    $locale: SiteLocale!
    $officeLanguage: ItemId
    $skip: IntType
    $tagId: ItemId
  ) {
    randomOffices: allOffices(
      locale: $locale
      first: 3
      skip: $skip
      orderBy: _createdAt_ASC
      filter: {
        hideOfficeFromOverview: { eq: "false" }
        parent: { exists: "false" }
        blockSearchIndexing: { eq: "false" }
        _locales: { allIn: [$locale] }
        officeLanguage: { anyIn: [$officeLanguage] }
        OR: [{ services: { anyIn: [$tagId] } }, { expertises: { anyIn: [$tagId] } }]
      }
    ) {
      ...OfficeFields
      ...OfficeIntroTeaserImage
    }
  }
`;

export const OFFICES_COUNT_QUERY = gql`
  query GetOfficesCount($locale: SiteLocale!, $officeLanguage: ItemId, $tagId: ItemId) {
    officesCount: _allOfficesMeta(
      filter: {
        hideOfficeFromOverview: { eq: "false" }
        parent: { exists: "false" }
        _locales: { allIn: [$locale] }
        officeLanguage: { anyIn: [$officeLanguage] }
        OR: [{ services: { anyIn: [$tagId] } }, { expertises: { anyIn: [$tagId] } }]
      }
    ) {
      count
    }
  }
`;
